import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { TouchTarget } from './button';
import { Link } from './link';

export function Navbar({ className, ...props }: React.ComponentPropsWithoutRef<'nav'>) {
  return <nav {...props} className={clsx(className, 'flex flex-1 items-center gap-4 py-2.5')} />;
}

export function NavbarDivider({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return <div aria-hidden="true" {...props} className={clsx(className, 'h-6 w-px bg-border')} />;
}

interface IndicatorState {
  left: number;
  width: number;
}

interface NavbarSectionContextValue {
  registerItem: (el: HTMLSpanElement, current: boolean) => void;
}

const NavbarSectionContext = createContext<NavbarSectionContextValue | null>(null);

export function NavbarSection({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLSpanElement>(null);
  const hasAnimatedRef = useRef(false);

  const registerItem = useCallback((el: HTMLSpanElement, current: boolean) => {
    if (!current || !containerRef.current || !indicatorRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const itemRect = el.getBoundingClientRect();
    const left = itemRect.left - containerRect.left + 8;
    const width = itemRect.width - 16;

    if (!hasAnimatedRef.current) {
      hasAnimatedRef.current = true;
      indicatorRef.current.style.transition = 'none';
      requestAnimationFrame(() => {
        if (indicatorRef.current) {
          indicatorRef.current.style.transition =
            'left 0.25s cubic-bezier(0.4, 0, 0.2, 1), width 0.25s cubic-bezier(0.4, 0, 0.2, 1)';
        }
      });
    }

    indicatorRef.current.style.left = `${left}px`;
    indicatorRef.current.style.width = `${width}px`;
  }, []);

  const ctxValue = React.useMemo(() => ({ registerItem }), [registerItem]);

  return (
    <NavbarSectionContext.Provider value={ctxValue}>
      <div
        {...props}
        ref={containerRef}
        className={clsx(className, 'relative flex items-center gap-3')}
      >
        {props.children}
        <span
          ref={indicatorRef}
          className="absolute -bottom-2.5 h-0.5 rounded-full bg-foreground"
        />
      </div>
    </NavbarSectionContext.Provider>
  );
}

export function NavbarSpacer({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return <div aria-hidden="true" {...props} className={clsx(className, '-ml-4 flex-1')} />;
}

export const NavbarItem = forwardRef(function NavbarItem(
  {
    current,
    className,
    children,
    ...props
  }: { current?: boolean; className?: string; children: React.ReactNode } & (
    | ({ href?: never } & Omit<Headless.ButtonProps, 'as' | 'className'>)
    | ({ href: string } & Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'>)
  ),
  ref: React.ForwardedRef<HTMLAnchorElement | HTMLButtonElement>
) {
  const spanRef = useRef<HTMLSpanElement>(null);
  const ctx = useContext(NavbarSectionContext);

  useEffect(() => {
    if (spanRef.current && ctx) {
      ctx.registerItem(spanRef.current, !!current);
    }
  }, [current, ctx]);

  let classes = clsx(
    // Base
    'relative flex min-w-0 items-center gap-3 rounded-md p-2 text-left text-base/6 font-medium text-foreground sm:text-sm/5',
    // Leading icon/icon-only
    '*:data-[slot=icon]:size-6 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:fill-muted-foreground sm:*:data-[slot=icon]:size-5',
    // Trailing icon (down chevron or similar)
    '*:not-nth-2:last:data-[slot=icon]:ml-auto *:not-nth-2:last:data-[slot=icon]:size-5 sm:*:not-nth-2:last:data-[slot=icon]:size-4',
    // Avatar
    '*:data-[slot=avatar]:-m-0.5 *:data-[slot=avatar]:size-7 *:data-[slot=avatar]:[--avatar-radius:var(--radius-md)] sm:*:data-[slot=avatar]:size-6',
    // Hover
    'data-hover:bg-accent data-hover:*:data-[slot=icon]:fill-foreground',
    // Active
    'data-active:bg-accent data-active:*:data-[slot=icon]:fill-foreground'
  );

  return (
    <span ref={spanRef} className={clsx(className, 'relative')}>
      {typeof props.href === 'string' ? (
        <Link
          {...props}
          className={classes}
          data-current={current ? 'true' : undefined}
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
        >
          <TouchTarget>{children}</TouchTarget>
        </Link>
      ) : (
        <Headless.Button
          {...props}
          className={clsx('cursor-pointer', classes)}
          data-current={current ? 'true' : undefined}
          ref={ref}
        >
          <TouchTarget>{children}</TouchTarget>
        </Headless.Button>
      )}
    </span>
  );
});

export function NavbarLabel({ className, ...props }: React.ComponentPropsWithoutRef<'span'>) {
  return <span {...props} className={clsx(className, 'truncate')} />;
}
